import { Injectable, signal } from '@angular/core';
import { MenuConfigItem } from '../models/survey.interface';

const FORM_INSTANCE_KEY = 'form.instanceId';
const SUBSCRIPTION_PLAN_KEY = 'organization.subscriptionPlanId';
const SUBSCRIPTION_PLAN_KEY_NAME = 'organization.subscriptionPlanKey';

/**
 * Service responsible for managing the selected form instance state
 * and the organization's subscription plan.
 * This is used across the application to track which form instance
 * is currently selected for data filtering in API calls.
 * The state is persisted in localStorage to survive page reloads.
 */
@Injectable({
  providedIn: 'root',
})
export class FormInstanceStateService {
  private selectedFormInstanceIdSignal = signal<number | null>(
    this.loadFromStorage(FORM_INSTANCE_KEY),
  );
  readonly selectedFormInstanceId = this.selectedFormInstanceIdSignal.asReadonly();

  private subscriptionPlanIdSignal = signal<number | null>(
    this.loadFromStorage(SUBSCRIPTION_PLAN_KEY),
  );
  readonly subscriptionPlanId = this.subscriptionPlanIdSignal.asReadonly();

  private subscriptionPlanKeySignal = signal<string | null>(
    localStorage.getItem(SUBSCRIPTION_PLAN_KEY_NAME),
  );
  readonly subscriptionPlanKey = this.subscriptionPlanKeySignal.asReadonly();

  private menuItemsSignal = signal<MenuConfigItem[]>([]);
  readonly menuItems = this.menuItemsSignal.asReadonly();

  private organizationDescriptionSignal = signal<string | null>(null);
  readonly organizationDescription = this.organizationDescriptionSignal.asReadonly();

  /**
   * Loads a numeric ID from localStorage by key.
   * @returns The stored ID or null if not found
   */
  private loadFromStorage(key: string): number | null {
    const stored = localStorage.getItem(key);
    if (stored) {
      const parsed = parseInt(stored, 10);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  }

  /**
   * Sets the selected form instance ID.
   * This ID is used in API calls to filter data by form instance.
   * The ID is persisted in localStorage.
   * @param id The form instance ID to select
   */
  setSelectedFormInstanceId(id: number): void {
    this.selectedFormInstanceIdSignal.set(id);
    localStorage.setItem(FORM_INSTANCE_KEY, id.toString());
  }

  /**
   * Gets the current selected form instance ID.
   * @returns The selected form instance ID or null if none selected
   */
  getSelectedFormInstanceId(): number | null {
    return this.selectedFormInstanceIdSignal();
  }

  /**
   * Sets the subscription plan ID from the organization config.
   * Persisted in localStorage.
   * @param id The subscription plan ID
   */
  setSubscriptionPlanId(id: number): void {
    this.subscriptionPlanIdSignal.set(id);
    localStorage.setItem(SUBSCRIPTION_PLAN_KEY, id.toString());
  }

  setSubscriptionPlanKey(key: string): void {
    this.subscriptionPlanKeySignal.set(key);
    localStorage.setItem(SUBSCRIPTION_PLAN_KEY_NAME, key);
  }

  getSubscriptionPlanKey(): string | null {
    return this.subscriptionPlanKeySignal();
  }

  /**
   * Gets the current subscription plan ID.
   * @returns The subscription plan ID or null if not set
   */
  getSubscriptionPlanId(): number | null {
    return this.subscriptionPlanIdSignal();
  }

  setMenuItems(items: MenuConfigItem[]): void {
    this.menuItemsSignal.set(items);
  }

  setOrganizationDescription(description: string | null): void {
    this.organizationDescriptionSignal.set(description);
  }

  getOrganizationDescription(): string | null {
    return this.organizationDescriptionSignal();
  }

  /**
   * Clears only the selected form instance ID (signal + localStorage), leaving
   * the subscription plan and menu untouched. Used when the current session has
   * no form instances, so no stale ID from a previous organization survives.
   */
  clearSelectedFormInstance(): void {
    this.selectedFormInstanceIdSignal.set(null);
    localStorage.removeItem(FORM_INSTANCE_KEY);
  }

  /**
   * Clears the selected form instance ID and subscription plan ID.
   * Also removes them from localStorage.
   */
  clearSelectedFormInstanceId(): void {
    this.selectedFormInstanceIdSignal.set(null);
    this.subscriptionPlanIdSignal.set(null);
    this.subscriptionPlanKeySignal.set(null);
    this.menuItemsSignal.set([]);
    this.organizationDescriptionSignal.set(null);
    localStorage.removeItem(FORM_INSTANCE_KEY);
    localStorage.removeItem(SUBSCRIPTION_PLAN_KEY);
    localStorage.removeItem(SUBSCRIPTION_PLAN_KEY_NAME);
  }
}
